import { useEffect, useMemo, useRef } from 'react';
import { Logo } from '@/components/Logo';
import { PlatformIcon } from '@/components/PlatformIcon';
import { useFeatureGate } from '@/features/billing/gates';
import {
  getAddSourceParams,
  useCustomizeParams,
} from '@/features/experiences/components/CustomizeMode/useCustomizeParams';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useOutlookConsentStatus } from '@/features/interactions/addToOutlook/hooks/useOutlookConsentStatus';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useLauncherAction } from '@/hooks/useLauncher';
import { FeedOriginKind, PlatformTypes } from '@/types/gql.generated';
import { Box, Button, Flex, Icon, Text, useToast } from '@/ui';
import { AgendaHeroSource } from './AgendaHeroSource';
import { GoogleCalendarSource } from './GoogleCalendarSource';
import { IcsFeedSource } from './IcsFeedSource';
import { OutlookCalendarSource } from './OutlookCalendarSource';
import { SourceChoice } from './SourceChoice';

// The active add sub-view is driven by the `source` URL param
// (add:EXPERIENCE / add:GOOGLE / add:OUTLOOK / add:ICS). Agenda Hero adds another
// experience as a feed; Google, Outlook and ICS connect external calendars — each
// via its own sub-view.

// Connecting an outside calendar is the gated part; pulling in another Agenda Hero
// experience stays free. The same list decides what the allowance counts, so an
// Agenda Hero feed never spends a free slot.
const EXTERNAL_CHANNELS: FeedOriginKind[] = [
  FeedOriginKind.Google,
  FeedOriginKind.Outlook,
  FeedOriginKind.Ics,
];

type Props = {
  onCancel: () => void;
};

export const AddSourceView = ({ onCancel }: Props) => {
  const { source, openAddSource, openAddChannel, selectFeed } =
    useCustomizeParams();
  const { experience } = usePageExperienceContext();
  const channel = source?.mode === 'add' ? source.channel : null;
  const toast = useToast();
  const { value: outlookEnabled } = useFeatureFlag('outlookIntegrationEnabled');
  const { connectErrorMessage, isAdminConsentRequired, clearConsentCode } =
    useOutlookConsentStatus();
  // Where checkout returns a user who upgrades from here. The redirect is built from
  // the pathname alone, so without this they land back on a bare edit URL with no
  // panel open and have to find their way to Add source again. A channel already in
  // the URL is a gated deep link (handled below), so resume that connect sub-view
  // rather than the picker they never chose.
  //
  // Memoized because it feeds the gate's promptUpgrade → requireAccess identity, and
  // that is a dependency of the deep-link effect below.
  const redirectParams = useMemo(() => getAddSourceParams(channel), [channel]);

  // What the free allowance is spent on. Feeds ride along on the experience, so
  // this is the same data the sources rail renders and needs no query of its own.
  const externalSourceCount = useMemo(
    () =>
      experience.feeds.filter((feed) => EXTERNAL_CHANNELS.includes(feed.kind))
        .length,
    [experience.feeds]
  );

  const {
    isGated: isExternalSourceGated,
    isLocked: isExternalSourceLocked,
    requireAccess: requireSourceAccess,
  } = useFeatureGate('externalSource', {
    count: externalSourceCount,
    redirectParams,
  });

  // The channel is a URL param, so the connect sub-views need the same check the
  // choice rows do — otherwise ?source=add:GOOGLE walks straight past the gate.
  const isExternalChannel =
    channel != null && EXTERNAL_CHANNELS.includes(channel);

  // The gate decides who gets *into* a connect sub-view, not who may stay in one.
  // Adding a source is what spends the allowance, so the count closes the gate while
  // the user is still standing in the form that just succeeded: without this latch
  // they get bounced back to the picker with an upgrade modal over the source they
  // just created. Recording the channel while it is admitted keeps the sub-view open
  // for the rest of its life, and the reset makes the next entry gated again.
  const admittedChannel = useRef<FeedOriginKind | null>(null);
  if (!isExternalChannel) {
    admittedChannel.current = null;
  } else if (!isExternalSourceLocked) {
    admittedChannel.current = channel;
  }
  const isChannelAdmitted =
    isExternalChannel && admittedChannel.current === channel;

  const isChannelBlocked =
    isExternalChannel && isExternalSourceLocked && !isChannelAdmitted;

  // Send a deep link that turned out to be gated back to the picker and explain
  // why. Clearing the param makes this self-terminating: the next render has no
  // channel. Blocked-but-not-yet-gated (still loading) deliberately does nothing,
  // so an entitled user's deep link survives and resolves into the sub-view.
  const isChannelRejected =
    isExternalChannel && isExternalSourceGated && !isChannelAdmitted;

  useEffect(() => {
    if (isChannelRejected) {
      openAddSource();
      requireSourceAccess(() => undefined);
    }
  }, [isChannelRejected, openAddSource, requireSourceAccess]);

  // Confirm a successful Google connection after the OAuth redirect returns to this
  // view (the `source=add:GOOGLE` URL param is preserved across the round trip).
  useLauncherAction('source-google-connected', () => {
    toast.notify('🎉 Google account connected');
  });

  // The launch param resumes this view whether the callback succeeded or failed,
  // so announce success only when it carries no error code — otherwise a failed
  // connect claimed "connected" while no integration existed. Clearing the code
  // also stops it riding along into the next attempt's redirect.
  useLauncherAction('source-outlook-connected', () => {
    // A tenant that needs admin approval is a next step, not a failure: the
    // connect view renders a "Get admin approval" CTA off the same ?code=, so
    // explain what happened and leave the code in place for it.
    if (isAdminConsentRequired) {
      toast.notify(
        'Your organization needs an administrator to approve Agenda Hero before Outlook can connect.'
      );
      return;
    }

    if (connectErrorMessage) {
      toast.error(connectErrorMessage);
    } else {
      toast.notify('🎉 Outlook account connected');
    }
    clearConsentCode();
  });

  if (channel === FeedOriginKind.Experience) {
    return <AgendaHeroSource onBack={openAddSource} />;
  }

  if (channel === FeedOriginKind.Google && !isChannelBlocked) {
    return <GoogleCalendarSource onAdded={selectFeed} onBack={openAddSource} />;
  }

  // The flag has to gate the sub-view too, not just the choice row, or
  // ?source=add:OUTLOOK starts an OAuth trip for a disabled integration.
  // Falling through to the picker (with the Outlook row hidden) is deliberate:
  // useFeatureFlag reports the `false` default while it loads, so bouncing the
  // param here would break a flag-on user's deep link — including the one
  // preserved across the OAuth round trip.
  if (
    channel === FeedOriginKind.Outlook &&
    outlookEnabled &&
    !isChannelBlocked
  ) {
    return (
      <OutlookCalendarSource onAdded={selectFeed} onBack={openAddSource} />
    );
  }

  if (channel === FeedOriginKind.Ics && !isChannelBlocked) {
    return <IcsFeedSource onAdded={selectFeed} onBack={openAddSource} />;
  }

  return (
    <Box maxW="560px">
      <Flex align="center" justify="space-between" mb={6}>
        <Text
          color="ink.strong"
          fontSize="26px"
          fontWeight={700}
          letterSpacing="-0.02em"
        >
          Add a source
        </Text>
        <Button
          color="ink.soft"
          fontSize="ah-control"
          fontWeight={600}
          h="auto"
          minW={0}
          p={0}
          variant="link"
          onClick={onCancel}
        >
          Cancel
        </Button>
      </Flex>

      <SourceChoice
        description="Add another Agenda Hero experience as a source"
        icon={<Logo height={20} />}
        iconBg="rgba(74,48,224,.1)"
        title="Agenda Hero"
        onClick={() => openAddChannel(FeedOriginKind.Experience)}
      />
      <SourceChoice
        description="Sign in and pick which calendars to pull"
        iconBg="rgba(44,136,217,.12)"
        isLocked={isExternalSourceLocked}
        title="Connect Google Calendar"
        icon={
          <PlatformIcon platform={PlatformTypes.GoogleIntegration} size={20} />
        }
        onClick={() =>
          requireSourceAccess(() => openAddChannel(FeedOriginKind.Google))
        }
      />
      {outlookEnabled && (
        <SourceChoice
          description="Sign in and pick which calendars to pull"
          iconBg="rgba(0,120,212,.12)"
          isLocked={isExternalSourceLocked}
          title="Connect Outlook Calendar"
          icon={
            <PlatformIcon
              platform={PlatformTypes.OutlookIntegration}
              size={20}
            />
          }
          onClick={() =>
            requireSourceAccess(() => openAddChannel(FeedOriginKind.Outlook))
          }
        />
      )}
      <SourceChoice
        description="An ICS / webcal link from the PAC, boosters, school…"
        icon={<Icon color="#5D3FF6" icon="Link" size={5} />}
        iconBg="rgba(93,63,246,.1)"
        isLocked={isExternalSourceLocked}
        title="Paste a calendar link"
        onClick={() =>
          requireSourceAccess(() => openAddChannel(FeedOriginKind.Ics))
        }
      />
    </Box>
  );
};
